import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import SyncStatus from "./SyncStatus";
import SignOffReminder from "./SignOffReminder";
import {
  LayoutDashboard, Clock, Users, UserSquare, FileText, Briefcase,
  Wrench, FileSpreadsheet, LogOut, ClipboardCheck, Languages, GrapeIcon, BadgeCheck, MapPin, UploadCloud, Menu, X, KeyRound, BarChart3, Tractor, ShieldAlert, Receipt, Car, Leaf, ListChecks, Building2, Bug, SprayCan, Grape, PiggyBank, Droplets, HardHat, Hammer, TreePine,
} from "lucide-react";

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef(null);

  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0 });
  }, [location.pathname, location.search]);
  const role = user?.role;
  const [moreOpen, setMoreOpen] = useState(false);
  const logoUrl = localStorage.getItem("vineops_logo") || "/vineops-logo-v2.png";

  const items = [
    { to: "/", label: t("nav.dashboard"), icon: LayoutDashboard, roles: ["admin", "supervisor", "foreman", "employee"] },
    { to: "/my-timecard", label: t("nav.my_timecard"), icon: Tractor, roles: ["tractor_driver", "irrigator"] },
    { to: "/pest/orders", label: t("nav.spray_orders"), icon: SprayCan, roles: ["tractor_driver"] },
    { to: "/timecard", label: t("nav.punch_crew"), icon: Clock, roles: ["admin", "supervisor", "foreman"] },
    { to: "/scouting", label: t("nav.scouting"), icon: Leaf, roles: ["admin", "supervisor"] },
    { to: "/pest", label: t("nav.pest"), icon: Bug, roles: ["admin", "supervisor"] },
    { to: "/crop", label: t("nav.crop"), icon: Grape, roles: ["admin", "supervisor"] },
    { to: "/worklist", label: t("nav.worklist"), icon: ListChecks, roles: ["admin", "supervisor", "foreman", "irrigator"] },
    { to: "/irrigation", label: t("nav.irrigation"), icon: Droplets, roles: ["admin", "supervisor"] },
    { to: "/irrigation/log", label: t("nav.irrigation_log"), icon: Droplets, roles: ["irrigator"] },
    { to: "/crews", label: t("nav.crews"), icon: Users, roles: ["admin", "supervisor", "foreman"] },
    { to: "/signoff", label: t("nav.signoff"), icon: ClipboardCheck, roles: ["admin", "supervisor", "foreman", "employee", "tractor_driver", "irrigator"] },
    { to: "/training", label: t("nav.training"), icon: HardHat, roles: ["admin", "supervisor", "foreman"] },
    { to: "/expenses", label: t("nav.expenses"), icon: Receipt, roles: ["admin", "supervisor", "foreman", "tractor_driver", "irrigator"] },
    { to: "/mileage", label: t("nav.mileage"), icon: Car, roles: ["admin", "supervisor", "foreman", "tractor_driver", "irrigator"] },
    { to: "/employees", label: t("nav.employees"), icon: UserSquare, roles: ["admin", "supervisor"] },
    { to: "/jobcodes", label: t("nav.jobcodes"), icon: Briefcase, roles: ["admin", "supervisor"] },
    { to: "/equipment", label: t("nav.equipment"), icon: Wrench, roles: ["admin", "supervisor"] },
    { to: "/equipment-report", label: t("nav.equip_reports"), icon: BarChart3, roles: ["admin", "supervisor"] },
    { to: "/break-audit", label: t("nav.break_audit"), icon: ShieldAlert, roles: ["admin", "supervisor"] },
    { to: "/ranches", label: t("nav.ranches"), icon: MapPin, roles: ["admin", "supervisor"] },
    { to: "/clients", label: t("nav.clients"), icon: Building2, roles: ["admin", "supervisor"] },
    { to: "/client-requests", label: t("nav.client_requests"), icon: Hammer, roles: ["admin", "supervisor"] },
    { to: "/estate", label: t("nav.estate"), icon: TreePine, roles: ["admin", "supervisor", "foreman"] },
    { to: "/portal", label: t("nav.portal"), icon: Grape, roles: ["client"] },
    { to: "/portal/requests", label: "Request Work", icon: Hammer, roles: ["client"] },
    { to: "/client-logins", label: t("nav.client_logins"), icon: KeyRound, roles: ["admin", "supervisor"] },
    { to: "/budgets", label: t("nav.budgets"), icon: PiggyBank, roles: ["admin"] },
    { to: "/import", label: t("nav.import"), icon: UploadCloud, roles: ["admin"] },
    { to: "/users", label: t("nav.users"), icon: KeyRound, roles: ["admin"] },
    { to: "/timesheets", label: t("nav.timesheets"), icon: FileText, roles: ["admin", "supervisor", "foreman"] },
    { to: "/approvals", label: t("nav.approvals"), icon: BadgeCheck, roles: ["admin", "supervisor", "foreman"] },
    { to: "/export", label: t("nav.qb_export"), icon: FileSpreadsheet, roles: ["admin", "supervisor"] },
  ].filter((i) => i.roles.includes(role));

  const onPickLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      localStorage.setItem("vineops_logo", reader.result);
      window.location.reload();
    };
    reader.readAsDataURL(f);
  };

  const brand = (
    <label className="cursor-pointer flex items-center" title="Click to upload your company logo">
      <input type="file" accept="image/*" onChange={onPickLogo} className="hidden" data-testid="logo-upload-input" />
      <img src={logoUrl} alt="VineOps by Vinedresser" className="h-44 w-auto object-contain" />
    </label>
  );

  return (
    <div className="h-screen overflow-hidden bg-[#F8F6F2] text-[#252525] flex flex-col" style={{ fontFamily: "'Inter Tight', system-ui, sans-serif" }}>
      {/* Top bar */}
      <header className="border-b border-stone-200/80 bg-[#F8F6F2] sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 sm:px-8 h-48">
          {brand}
          <div className="flex items-center gap-2 sm:gap-3">
            <SyncStatus />
            <button
              data-testid="lang-toggle-btn"
              onClick={() => setLang(lang === "en" ? "es" : "en")}
              className="h-10 px-3 bg-white border border-stone-200 text-sm font-medium hover:bg-stone-50 flex items-center gap-1.5 text-stone-700"
              title="Toggle language"
            >
              <Languages size={16} /> {lang === "en" ? "EN" : "ES"}
            </button>
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="font-semibold text-sm">{user?.name}</span>
              <span className="text-[10px] uppercase tracking-wide text-stone-500">{role}</span>
            </div>
            <button
              data-testid="logout-btn"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="h-10 px-3 bg-white border border-stone-200 text-sm font-medium text-stone-700 hover:bg-[#1F3D34] hover:text-white hover:border-[#1F3D34] flex items-center gap-1.5"
            >
              <LogOut size={16} /> {t("auth.exit")}
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar (desktop) — own scroll container, independent of the main page */}
        <aside className="hidden md:flex flex-col w-64 border-r border-stone-200/80 p-4 bg-[#F8F6F2] overflow-y-auto overscroll-contain" data-testid="shell-sidebar">
          <nav className="space-y-1 flex-1">
            {items.map((i) => (
              <NavLink
                key={i.to}
                to={i.to}
                end={i.to === "/"}
                data-testid={`nav-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isActive
                      ? "bg-[#1F3D34] text-white shadow-sm shadow-[#1F3D34]/20"
                      : "text-stone-600 hover:bg-stone-100 hover:text-[#1F3D34]"
                  }`
                }
              >
                <i.icon size={18} strokeWidth={1.8} /> {i.label}
              </NavLink>
            ))}
          </nav>
          <div className="pt-4 mt-4 border-t border-stone-200/80 text-[10px] text-stone-400 leading-relaxed">
            © {new Date().getFullYear()} Vinedresser Vineyard<br />Management, INC.
          </div>
        </aside>

        <main ref={mainRef} className="flex-1 overflow-y-auto p-4 sm:p-8 pb-28 md:pb-8" data-testid="shell-main">
          <div className="max-w-[1400px]">
            <SignOffReminder />
            <div>{children}</div>
          </div>
        </main>
      </div>

      {/* Footer (desktop) */}
      <footer className="hidden md:block border-t border-stone-200/80 bg-[#F8F6F2] px-8 py-3 text-[11px] text-stone-500 flex-shrink-0">
        <div className="flex justify-between items-center">
          <span>© {new Date().getFullYear()} Vinedresser Vineyard Management, INC. · All rights reserved.</span>
          <span style={{ fontFamily: "'Fraunces', serif" }} className="italic text-stone-400">Manage crews, scouting, scheduling, work orders, spray programs, compliance, and client reporting from one connected platform.</span>
        </div>
      </footer>

      {/* Bottom nav (mobile / tablet) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[#F8F6F2]/95 backdrop-blur-md border-t border-stone-200/80 grid grid-cols-5 z-40 pb-safe">
        {items.slice(0, 4).map((i) => (
          <NavLink
            key={i.to}
            to={i.to}
            end={i.to === "/"}
            data-testid={`bottom-nav-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-1 h-16 text-[10px] font-medium ${
                isActive ? "text-[#1F3D34]" : "text-stone-500"
              }`
            }
          >
            <i.icon size={22} strokeWidth={1.8} />
            <span>{i.label.split(" ")[0]}</span>
          </NavLink>
        ))}
        <button
          data-testid="bottom-nav-more"
          onClick={() => setMoreOpen(true)}
          className="flex flex-col items-center justify-center gap-1 h-16 text-[10px] font-medium text-stone-500"
        >
          <Menu size={22} strokeWidth={1.8} />
          <span>{t("nav.more")}</span>
        </button>
      </nav>

      {/* More menu drawer (mobile / tablet) */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex flex-col justify-end" data-testid="more-menu-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMoreOpen(false)} />
          <div className="relative bg-[#F8F6F2] border-t border-stone-200 rounded-t-2xl max-h-[80vh] overflow-y-auto pb-safe">
            <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200 sticky top-0 bg-[#F8F6F2]">
              <span className="font-semibold text-lg tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{t("nav.menu")}</span>
              <button data-testid="more-menu-close" onClick={() => setMoreOpen(false)} className="h-10 w-10 flex items-center justify-center border border-stone-200 rounded-lg">
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 p-4">
              {items.map((i) => (
                <NavLink
                  key={i.to}
                  to={i.to}
                  end={i.to === "/"}
                  onClick={() => setMoreOpen(false)}
                  data-testid={`more-nav-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-4 rounded-xl text-sm font-medium border ${
                      isActive
                        ? "bg-[#1F3D34] text-white border-[#1F3D34]"
                        : "bg-white text-stone-700 border-stone-200"
                    }`
                  }
                >
                  <i.icon size={20} strokeWidth={1.8} /> {i.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
